import { Grid2 } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import ModalComponent from '../../../../components/ModalComponent';
import zod from 'zod';
import { UserFormSchema } from '../../../../services/UserService/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import DropdownSelectComponent from '../../../../components/DropdownSelectComponent';
import { useGetAllRoleByLicenseAsync } from '../../../../services/RoleService/hooks/useMutation';
import { useContext, useEffect, useState } from 'react';
import { IRoleSimpleProps } from '../../../../services/RoleService/types';
import DropdownComponent from '../../../../components/DropdownComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import { AppContext } from '../../../../context/AppContextProvider';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../../components/DropSelectComponent';
import {
  useCreateUserAsync,
  useUpdateUserAsync,
} from '../../../../services/UserService/hooks/useMutation';

interface UserEditionModalProps {
  isOpen: boolean;
  userToEdit?: any;
  setIsOpen: (isOpen: boolean) => void;
  handleSaveForm: () => void;
}

type UserEditionFormInput = zod.infer<typeof UserFormSchema>;

const UserEditionModal = ({
  isOpen,
  setIsOpen,
  userToEdit,
  handleSaveForm,
}: UserEditionModalProps) => {
  const { showSnackbar } = useContext(AppContext);
  const [personType, setPersonType] = useState(0);
  const [roles, setRoles] = useState<IRoleSimpleProps[]>([]);
  const { control, reset, setValue, handleSubmit, getValues } =
    useForm<UserEditionFormInput>({
      resolver: zodResolver(UserFormSchema),
      defaultValues: {
        name: '',
        cpf: '',
        cnpj: '',
        email: '',
        phone: '',
        password: '',
        roleIds: [],
      },
    });

  const { mutateAsync: mutateGetAllRolesAsync } = useGetAllRoleByLicenseAsync();
  const [key, setKey] = useState(0);

  const { mutate: mutateCreateUserAsync, isLoading: isLoadingCreateUser } =
    useCreateUserAsync();
  const { mutate: mutateUpdateUserAsync, isLoading: isLoadingUpdateUser } =
    useUpdateUserAsync();

  const handleSave = (data: UserEditionFormInput) => {
    if (data.phone) data.phone = data.phone.replace(/\D/g, '');

    if (data?.id) {
      mutateUpdateUserAsync(
        { id: data.id, data },
        {
          onSuccess: () =>
            resetAndShowMessage('Usuário atualizado com sucesso.'),
        },
      );
      return;
    }
    mutateCreateUserAsync(data, {
      onSuccess: () => {
        resetAndShowMessage('Usuário criado com sucesso.');
      },
    });
  };

  const getUserSelectionOptions = (): IDropSelectOptions[] => {
    return roles.map((i) => {
      return {
        option: i.name,
        value: i.id,
      } as IDropSelectOptions;
    });
  };

  const resetAndShowMessage = (message: string) => {
    showSnackbar(message, 'success');
    reset();
    setIsOpen(false);
    handleSaveForm();
  };

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  useEffect(() => {
    setKey(Math.random() * 10);
  }, []);

  useEffect(() => {
    mutateGetAllRolesAsync().then(setRoles);
  }, []);

  useEffect(() => {
    if (userToEdit) reset(userToEdit, { keepDefaultValues: true });
  }, [userToEdit]);
  return (
    <ModalComponent
      title={'Edição do usuário'}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <Grid2 container columnSpacing={3}>
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Nome"
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Email"
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'email'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Senha"
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>

        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Telefone"
                value={value}
                mask={'(00) 0 0000-0000'}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'phone'}
            control={control}
          />
        </Grid2>
        <Grid2 size={6}>
          <DropdownComponent
            label="Tipo de pessoa"
            value={personType}
            onChange={(val) => {
              if (val == 0) setValue('cnpj', '');
              else setValue('cpf', '');
              setPersonType(val);
            }}
            options={[
              { label: 'Pessoa física', value: 0 },
              { label: 'Pessoa jurídica', value: 1 },
            ]}
            optionLabel={'label'}
            optionValue={'value'}
          />
        </Grid2>
        {personType === 0 ? (
          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="CPF"
                  value={value}
                  mask={'000.000.000-00'}
                  errorMessage={error?.message}
                  onChangeText={onChange}
                />
              )}
              name={'cpf'}
              control={control}
            />
          </Grid2>
        ) : (
          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="CNPJ"
                  value={value}
                  mask={'**.***.***/****-00'}
                  errorMessage={error?.message}
                  onChangeText={onChange}
                />
              )}
              name={'cnpj'}
              control={control}
            />
          </Grid2>
        )}
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropSelectComponent
                label="Cargos"
                valueSelected={value}
                isRequired
                key={key}
                errorMessage={error?.message}
                onChange={(value) => {
                  onChange(value);
                }}
                isError={!!error}
                isMultiple
                options={getUserSelectionOptions()}
              />
            )}
            name={'roleIds'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={isLoadingCreateUser || isLoadingUpdateUser}
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default UserEditionModal;
